<template>
  <div class="col-12">
    <div class="mb-4 ps-3 no-print d-flex justify-content-between">
      <div>
        <button
          class="btn btn-primary btn-sm"
          @click="printSchedule"
          :disabled="loading"
          :aria-busy="loading ? 'true' : 'false'"
        >
          <i class="fas fa-print ms-2"></i>
          {{ $t('schedule.print') }}
        </button>
      </div>
      <div>
        <router-link
          :to="{ name: 'GroupScheduleSettings', params: { classId: $route.params.classId, groupId: groupId } }"
          class="btn btn-secondary btn-sm"
          :class="{ disabled: loading }"
          :aria-disabled="loading ? 'true' : 'false'"
          @click.prevent="loading ? null : undefined"
            v-can="'class_schedules.update'"

        >
          <i class="fas fa-cog ms-2"></i>
          {{ $t('schedule.manage') }}
        </router-link>
      </div>
    </div>

    <div class="card mb-5" style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px;">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">
              {{ $t('schedule.title', { group: groupName }) }}
            </h6>
          </div>
        </div>
      </div>

      <div class="card-body px-0 pb-2 pe-3">
        <div class="table-responsive p-4">
          <!-- Loader -->
          <LoadingSpinner
            v-if="loading"
            :message="$t('schedule.loading')"
            class="py-3 py-5"
          />

          <!-- Content -->
          <template v-else>
            <!-- Mobile View (لا تُطبع) -->
            <div class="d-block d-md-none mx-4">
              <div v-for="day in days" :key="'mobile-'+day" class="mb-4">
                <h5 class="fw-bold text-center mb-3">{{ $t(`schedule.days.${day}`) }}</h5>
                <div class="list-group">
                  <div
                    v-for="period in getPeriodsForDay(day)"
                    :key="'mobile-'+day+'-'+period.value"
                    class="list-group-item"
                    :class="{
                      'list-group-item-primary': hasSchedule(day, period.backendValue),
                      'list-group-item-secondary': period.value === $t('schedule.break')
                    }"
                  >
                    <div class="d-flex justify-content-between">
                      <span>{{ period.label }}</span>
                      <small class="text-muted">{{ period.time }}</small>
                    </div>
                    <div v-if="period.value !== $t('schedule.break')" class="mt-2">
                      {{ getSubjectName(day, period.backendValue) || '---' }}
                    </div>
                    <div v-else class="mt-2 text-center">
                      {{ $t('schedule.break') }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Desktop Table (المحتوى المطبوع) -->
            <div class="d-none d-md-block" id="schedule-to-print">
              <table class="table table-bordered text-center">
                <thead>
                  <tr>
                    <th class="bg-light">{{ $t('schedule.dayPeriod') }}</th>
                    <th
                      v-for="period in periodsWithTimes"
                      :key="period.value"
                      class="bg-light"
                    >
                      {{ period.label }}<br />
                      <small class="text-muted">{{ period.time }}</small>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="day in days" :key="day">
                    <td class="bg-light fw-bold">{{ $t(`schedule.days.${day}`) }}</td>

                    <template v-if="day === 'السبت' && !saturdayHasSchedules">
                      <td
                        :colspan="periodsWithTimes.length"
                        class="empty-day"
                        style="height: 100px; vertical-align: middle;"
                      >
                        {{ $t('schedule.noClasses') }}
                      </td>
                    </template>

                    <template v-else>
                      <td
                        v-for="period in getPeriodsForDay(day)"
                        :key="period.value"
                        :class="{
                          'text-primary fw-bold': hasSchedule(day, period.backendValue),
                          'bg-secondary text-white': period.value === $t('schedule.break')
                        }"
                      >
                        <template v-if="period.value === $t('schedule.break')">
                          {{ $t('schedule.break') }}
                        </template>
                        <template v-else>
                          {{ getSubjectName(day, period.backendValue) }}
                        </template>
                      </td>
                    </template>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, defineProps } from "vue";
import { useStore } from "vuex";
import { useToast } from "vue-toastification";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";

const { t: $t } = useI18n();
const route = useRoute();
const store = useStore();
const toast = useToast();

const props = defineProps({
  groupId: {
    type: [String, Number],
    required: true,
  },
  groupName: {
    type: String,
    required: true,
  },
});

const schedules = ref([]);
const loading = ref(false);

const scheduleConfig = ref({
  start_time: "08:00",
  period_duration: 45,
  total_periods: 6,
  break_duration: 30,
  break_after_period: 2
});

// الأيام (تتعامل مع المفاتيح من ملف الترجمة)
const days = ["السبت","الأحد","الاثنين","الثلاثاء","الأربعاء","الخميس"];

const periodsWithTimes = computed(() => {
  const periods = [];
  const startTime = new Date(`2000-01-01T${scheduleConfig.value.start_time}`);
  const periodDuration = scheduleConfig.value.period_duration;
  const breakDuration = scheduleConfig.value.break_duration;
  const totalPeriods = scheduleConfig.value.total_periods;
  const breakAfterPeriod = scheduleConfig.value.break_after_period;

  let currentTime = new Date(startTime.getTime());

  for (let i = 1; i <= totalPeriods; i++) {
    const periodEnd = new Date(currentTime.getTime() + periodDuration * 60000);

    const periodName = $t(`schedule.periodNames.${i}`);
    const backendPeriodName = getBackendPeriodName(i);

    periods.push({
      value: periodName,
      backendValue: backendPeriodName,
      label: $t('schedule.periodLabel', { num: periodName }),
      time: `${formatTime(currentTime)} - ${formatTime(periodEnd)}`
    });

    if (i === breakAfterPeriod) {
      const breakStart = new Date(periodEnd.getTime());
      const breakEnd = new Date(breakStart.getTime() + breakDuration * 60000);

      periods.push({
        value: $t('schedule.break'),
        backendValue: "استراحة",
        label: $t('schedule.break'),
        time: `${formatTime(breakStart)} - ${formatTime(breakEnd)}`
      });

      currentTime = new Date(breakEnd.getTime());
    } else {
      currentTime = new Date(periodEnd.getTime());
    }
  }

  return periods;
});

const saturdayHasSchedules = computed(() => {
  if (!Array.isArray(schedules.value)) return false;
  return schedules.value.some(s => s.day === "السبت");
});

const getPeriodsForDay = (day) => {
  if (day === "السبت" && !saturdayHasSchedules.value) {
    return [];
  }
  return periodsWithTimes.value;
};

const getBackendPeriodName = (number) => {
  const backendPeriodNames = {
    1: "الاولى",
    2: "الثانية",
    3: "الثالثة",
    4: "الرابعة",
    5: "الخامسة",
    6: "السادسة",
    7: "السابعة"
  };
  return backendPeriodNames[number] || number.toString();
};

const formatTime = (date) => {
  return date.toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit" });
};

const loadScheduleSettings = () => {
  try {
    const saved = localStorage.getItem(`schedule_config_${route.params.classId}_${props.groupId}`);
    if (saved) {
      scheduleConfig.value = { ...scheduleConfig.value, ...JSON.parse(saved) };
    }
  } catch (error) {
    console.log('No saved settings found, using defaults');
  }
};

const fetchSchedules = async () => {
  try {
    loading.value = true;
    const response = await store.dispatch("schedules/fetchGroupSchedules", {
      classId: route.params.classId,
      groupId: props.groupId
    });
    schedules.value = Array.isArray(response) ? response : [];
  } catch (error) {
    toast.error(error.response?.data?.message || $t('schedule.fetchError'));
    schedules.value = [];
  } finally {
    loading.value = false;
  }
};

const hasSchedule = (day, period) => {
  if (!Array.isArray(schedules.value)) return false;
  return schedules.value.some((s) => s.day === day && s.period === period);
};

const getSubjectName = (day, period) => {
  if (!Array.isArray(schedules.value)) return "";
  const schedule = schedules.value.find(
    (s) => s.day === day && s.period === period
  );
  return schedule?.subject?.name || "";
};

const printSchedule = () => {
  if (loading.value) return; // avoid printing while loading
  const wrapper = document.getElementById('schedule-to-print');
  const printContent = wrapper ? wrapper.innerHTML : '';
  const printWindow = window.open('', '_blank');

  printWindow.document.write(`
    <!DOCTYPE html>
    <html dir="rtl">
    <head>
      <title>${$t('schedule.title', { group: props.groupName })}</title>
      <meta charset="UTF-8">
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; }
        h2 { text-align: center; margin-bottom: 20px; color: #0d6efd; }
        table { width: 100%; border-collapse: collapse; margin: 0 auto; }
        th, td { padding: 8px; text-align: center; border: 1px solid #000; }
        .bg-light { background-color: #f8f9fa !important; }
        .bg-secondary { background-color: #6c757d !important; color: white !important; }
        .text-muted { color: #6c757d !important; }
        .empty-day { background-color: #f8f9fa !important; color: #6c757d !important; font-style: italic; }
        .print-footer { text-align: left; margin-top: 20px; font-size: 12px; }
        @media print { body { padding: 0; } }
      </style>
    </head>
    <body>
      <h2>${$t('schedule.title', { group: props.groupName })}</h2>
      ${printContent}
      <div class="print-footer">
        ${$t('schedule.printedAt', { time: new Date().toLocaleString() })}
      </div>
    </body>
    </html>
  `);

  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
};

onMounted(() => {
  loadScheduleSettings();
  fetchSchedules();
});
</script>

<style scoped>
.table-bordered td,
.table-bordered th {
  vertical-align: middle;
  height: 100px;
}

.empty-day {
  background-color: #f8f9fa !important;
  color: #6c757d !important;
  font-style: italic;
}

@media print {
  .no-print {
    display: none !important;
  }
}
</style>
